import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { PathChoice } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'

import {
    ArrowRight,
    Check,
    LoaderCircle,
    Phone
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { onPathChosen, takeStoredPath } from '@/lib/quote'
import { arrowClass, buttonClass } from '@/components/generals/button'

import {
    contact,
    contactSection,
    pathChoices
} from '@/constants/content'

type FieldName = 'name' | 'email' | 'message'
type FieldErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'sending' | 'sent' | 'error'

interface FormValues {
    name: string
    email: string
    phone: string
    path: PathChoice | ''
    message: string
    company: string
}

const EMPTY: FormValues = {
    name: '',
    email: '',
    phone: '',
    path: '',
    message: '',
    company: ''
}

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:".]{2,}$/

const inputClass = 'mt-2 block w-full rounded-lg border-2 border-ink bg-paper px-4 py-3.5 text-lg text-ink transition-[border-color,box-shadow] duration-200 placeholder:text-ink/40 focus-visible:border-blaze-deep focus-visible:shadow-[4px_4px_0_0_var(--color-blaze-deep)] focus-visible:outline-none aria-[invalid=true]:border-destructive'
const labelClass = 'font-heading text-base font-bold'

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (!values.name.trim()) errors.name = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (!values.message.trim()) errors.message = 'Please tell me a little about what you need.'

    return errors
}

const Required: FC = (): ReactNode => (
    <>
        <span aria-hidden='true' className='text-blaze-deep'> *</span>
        <span className='sr-only'> (required)</span>
    </>
)

const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [failure, setFailure] = useState('')
    const [sentName, setSentName] = useState('')
    const startedAt = useRef(0)
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        startedAt.current = Date.now()

        const stored = takeStoredPath()

        if (stored) setValues(current => ({ ...current, path: stored }))

        return onPathChosen(path => setValues(current => ({ ...current, path })))
    }, [])

    useEffect(() => {
        if (status === 'sent') successRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const nextErrors = validate(values)

        setErrors(nextErrors)
        setFailure('')

        const firstInvalid = (['name', 'email', 'message'] as Array<FieldName>).find(field => nextErrors[field])

        if (firstInvalid) {
            formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus()

            return
        }

        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, elapsed: Date.now() - startedAt.current })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: FieldErrors } | null

            if (response.ok && data?.ok) {
                setSentName(values.name.trim().split(/\s+/)[0] ?? '')
                setValues(EMPTY)
                setStatus('sent')

                return
            }

            if (response.status === 400 && data?.errors) {
                setErrors(data.errors)
                setStatus('idle')

                return
            }

            setFailure(response.status === 429 && data?.message ? data.message : '')
            setStatus('error')
        }

        catch {
            setFailure('')
            setStatus('error')
        }
    }

    const reset = () => {
        startedAt.current = Date.now()
        setStatus('idle')
    }

    if (status === 'sent') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='flex min-h-[28rem] flex-col items-start justify-center rounded-lg border-2 border-paper bg-paper p-6 text-ink shadow-[8px_8px_0_0_var(--color-blaze)] outline-none sm:p-10'
            >
                <motion.span
                    aria-hidden='true'
                    className='grid size-20 place-items-center rounded-full border-2 border-ink bg-blaze'
                    initial={{ scale: 0, rotate: -45 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 15 }}
                >
                    <Check className='size-10' strokeWidth={3} />
                </motion.span>

                <h3 className='mt-8 font-heading text-4xl font-extrabold tracking-[-0.03em] wdth-80 sm:text-5xl'>
                    Message sent.
                </h3>

                <p className='mt-4 max-w-[28rem] text-lg leading-relaxed text-ink/75'>
                    {`Thanks${sentName ? `, ${sentName}` : ''}. I’ll get back to you soon.`}
                </p>

                <button
                    type='button'
                    onClick={reset}
                    className='mt-8 font-heading text-base font-bold underline decoration-blaze decoration-2 underline-offset-4 hover:text-blaze-deep'
                >
                    Send another message
                </button>
            </div>
        )
    }

    const describedBy = (field: FieldName) => errors[field] ? `contact-${field}-error` : undefined

    return (
        <form
            ref={formRef}
            noValidate
            onSubmit={handleSubmit}
            aria-label='Contact form'
            className='relative rounded-lg border-2 border-paper bg-paper p-6 text-ink shadow-[8px_8px_0_0_var(--color-blaze)] [--focus-ring:var(--cobalt)] sm:p-10'
        >
            <div className='grid gap-6 sm:grid-cols-2'>
                <div>
                    <label htmlFor='contact-name' className={labelClass}>
                        {contactSection.fields.name}
                        <Required />
                    </label>

                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={120}
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={describedBy('name')}
                        className={inputClass}
                    />

                    {errors.name && (
                        <p id='contact-name-error' className='mt-2 text-sm font-semibold text-destructive'>
                            {errors.name}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor='contact-email' className={labelClass}>
                        {contactSection.fields.email}
                        <Required />
                    </label>

                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        maxLength={254}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email')}
                        className={inputClass}
                    />

                    {errors.email && (
                        <p id='contact-email-error' className='mt-2 text-sm font-semibold text-destructive'>
                            {errors.email}
                        </p>
                    )}
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor='contact-phone' className={labelClass}>
                        {contactSection.fields.phone}
                    </label>

                    <input
                        id='contact-phone'
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={40}
                        value={values.phone}
                        onChange={update('phone')}
                        className={inputClass}
                    />
                </div>

                <fieldset className='sm:col-span-2'>
                    <legend className={labelClass}>
                        {contactSection.fields.path}
                    </legend>

                    <div className='mt-3 grid gap-3 min-[420px]:grid-cols-2'>
                        {pathChoices.map((choice) => {
                            const selected = values.path === choice.id

                            return (
                                <label
                                    key={choice.id}
                                    className={cn(
                                        'relative flex cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3.5 text-base font-semibold leading-snug transition-[background-color,border-color,box-shadow,translate] duration-200 ease-snap active:translate-y-px',
                                        'has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-cobalt',
                                        selected ? 'border-ink bg-blaze shadow-[3px_3px_0_0_var(--color-ink)]' : 'border-ink/25 bg-paper hover:border-ink'
                                    )}
                                >
                                    <input
                                        type='radio'
                                        name='path'
                                        value={choice.id}
                                        checked={selected}
                                        onChange={() => setValues(current => ({ ...current, path: choice.id }))}
                                        className='sr-only'
                                    />

                                    <span
                                        aria-hidden='true'
                                        className={cn('grid size-5 shrink-0 place-items-center rounded-full border-2 border-ink transition-colors', selected ? 'bg-ink' : 'bg-transparent')}
                                    >
                                        {selected && <Check className='size-3 text-paper' strokeWidth={4} />}
                                    </span>

                                    {choice.label}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>

                <div className='sm:col-span-2'>
                    <label htmlFor='contact-message' className={labelClass}>
                        {contactSection.fields.message}
                        <Required />
                    </label>

                    <textarea
                        id='contact-message'
                        name='message'
                        required
                        rows={5}
                        maxLength={5000}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message')}
                        className={cn(inputClass, 'min-h-36 resize-y')}
                    />

                    {errors.message && (
                        <p id='contact-message-error' className='mt-2 text-sm font-semibold text-destructive'>
                            {errors.message}
                        </p>
                    )}
                </div>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                <label htmlFor='contact-company'>Company</label>

                <input
                    id='contact-company'
                    name='company'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.company}
                    onChange={update('company')}
                />
            </div>

            {status === 'error' && (
                <p role='alert' className='mt-6 rounded-lg border-2 border-destructive bg-destructive/10 px-4 py-3 text-base font-medium'>
                    {failure || 'Your message didn’t send. Please try again, or call '}

                    {!failure && (
                        <a href={contact.phoneHref} className='inline-flex items-center gap-1 font-bold underline underline-offset-2'>
                            <Phone aria-hidden='true' className='size-4' />
                            {contact.phone}
                        </a>
                    )}

                    {!failure && '.'}
                </p>
            )}

            <button
                type='submit'
                disabled={status === 'sending'}
                aria-busy={status === 'sending'}
                className={buttonClass({ size: 'lg', className: 'mt-8 w-full sm:w-auto' })}
            >
                {contactSection.submit}

                {status === 'sending'
                    ? <LoaderCircle aria-hidden='true' className='size-5 animate-spin' />
                    : <ArrowRight aria-hidden='true' className={arrowClass} />}
            </button>
        </form>
    )
}

export default ContactForm
